<template>
  <span>
    <button class="btn btn-primary" data-bs-toggle="modal" :data-bs-target="`#${modalId}`" @click="reset">
      <i class="fa-regular fa-square-plus"></i> New Credit / Refund
    </button>

    <div class="modal fade" :id="modalId" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Request a Credit or Refund</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">
              This only captures a request. Nothing moves until a second admin approves it (or, below
              the configured threshold, you may approve your own request from the approval queue).
            </p>

            <div class="mb-3">
              <label class="form-label">Kind <span class="text-danger">*</span></label>
              <select class="form-select" v-model="kind" @change="onKindChange">
                <option value="SUPPORT_CREDIT">Support credit</option>
                <option value="PROMOTIONAL_CREDIT">Promotional credit</option>
                <option value="MANUAL_ADJUSTMENT">Manual adjustment (credit-only)</option>
                <option value="TOP_UP_REFUND">Top-up refund</option>
                <option value="RIDE_REFUND">Ride refund</option>
              </select>
            </div>

            <div class="mb-3" v-if="kind === 'TOP_UP_REFUND'">
              <label class="form-label">Top-up <span class="text-danger">*</span></label>
              <select class="form-select" v-model="sourceTopUpId" @change="onTopUpChange">
                <option :value="null">-- Select a top-up --</option>
                <option v-for="topUp in refundableTopUps" :key="topUp.id" :value="topUp.id">
                  {{ formatDate(topUp.createdOn) }} &mdash; {{ formatMinor(topUp.amountMinor) }}
                  (refundable {{ formatMinor(topUp.refundableRemainderMinor) }})
                </option>
              </select>
              <div v-if="isLoadingTopUps" class="text-muted small mt-1">Loading top-ups...</div>
              <div v-else-if="refundableTopUps.length === 0" class="text-muted small mt-1">No refundable top-ups found.</div>
            </div>

            <div class="mb-3" v-if="kind === 'RIDE_REFUND'">
              <label class="form-label">Ride <span class="text-danger">*</span></label>
              <select class="form-select" v-model="sourceRideId" @change="onRideChange">
                <option :value="null">-- Select a settled ride --</option>
                <option v-for="ride in refundableRides" :key="ride.rideId" :value="ride.rideId">
                  {{ formatDate(ride.orderDateTime) }} &mdash; {{ formatMinor(ride.walletAmountMinor + ride.cardAmountMinor) }}
                </option>
              </select>
              <div v-if="isLoadingRides" class="text-muted small mt-1">Loading rides...</div>
              <div v-else-if="refundableRides.length === 0" class="text-muted small mt-1">No linked rides found.</div>
              <small class="form-text text-muted">Ride refunds are all-or-nothing in v1 — the amount below is fixed.</small>
            </div>

            <div class="mb-3">
              <label class="form-label">Amount (CAD) <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" min="0.01" step="0.01" v-model.number="amountDollars"
                       :disabled="kind === 'RIDE_REFUND'">
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Reason <span class="text-danger">*</span></label>
              <textarea class="form-control" rows="2" v-model="reason" required></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">Notes (optional)</label>
              <textarea class="form-control" rows="2" v-model="notes"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" :disabled="isSaving || !isValid" @click="submit">
              <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
              {{ isSaving ? 'Submitting...' : 'Submit Request' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </span>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Modal } from 'bootstrap';
import { showToast, notificationType } from '../utility/toastNotificationUtility';

const props = defineProps({
  walletId: { type: String, required: true }
});

const emit = defineEmits(['created']);

const modalId = computed(() => `wallet-adjustment-modal-${props.walletId}`);

const kind = ref('SUPPORT_CREDIT');
const amountDollars = ref(null);
const reason = ref('');
const notes = ref('');
const sourceTopUpId = ref(null);
const sourceRideId = ref(null);
const isSaving = ref(false);

const refundableTopUps = ref([]);
const isLoadingTopUps = ref(false);
const refundableRides = ref([]);
const isLoadingRides = ref(false);

const formatDate = (value) => value ? new Date(value).toLocaleString() : '';
const formatMinor = (value) => `$${((value || 0) / 100).toFixed(2)}`;

const isValid = computed(() => {
  if (!reason.value.trim() || !amountDollars.value || amountDollars.value <= 0) return false;
  if (kind.value === 'TOP_UP_REFUND' && !sourceTopUpId.value) return false;
  if (kind.value === 'RIDE_REFUND' && !sourceRideId.value) return false;
  return true;
});

const reset = () => {
  kind.value = 'SUPPORT_CREDIT';
  amountDollars.value = null;
  reason.value = '';
  notes.value = '';
  sourceTopUpId.value = null;
  sourceRideId.value = null;
};

const onKindChange = async () => {
  sourceTopUpId.value = null;
  sourceRideId.value = null;
  if (kind.value === 'RIDE_REFUND') {
    amountDollars.value = null;
  }
  if (kind.value === 'TOP_UP_REFUND' && refundableTopUps.value.length === 0) {
    isLoadingTopUps.value = true;
    try {
      const response = await fetch(`/api/admin/wallets/${props.walletId}/top-ups`);
      if (response.ok) {
        const all = await response.json();
        refundableTopUps.value = all.filter(t => t.refundableRemainderMinor > 0);
      }
    } catch (error) {
      console.error('Error loading top-ups for refund selection:', error);
    } finally {
      isLoadingTopUps.value = false;
    }
  }
  if (kind.value === 'RIDE_REFUND' && refundableRides.value.length === 0) {
    isLoadingRides.value = true;
    try {
      const response = await fetch(`/api/admin/wallets/${props.walletId}/ride-links?page=1`);
      if (response.ok) {
        const data = await response.json();
        refundableRides.value = data.items || [];
      }
    } catch (error) {
      console.error('Error loading rides for refund selection:', error);
    } finally {
      isLoadingRides.value = false;
    }
  }
};

const onTopUpChange = () => {
  const selected = refundableTopUps.value.find(t => t.id === sourceTopUpId.value);
  if (selected) {
    amountDollars.value = selected.refundableRemainderMinor / 100;
  }
};

const onRideChange = () => {
  const selected = refundableRides.value.find(r => r.rideId === sourceRideId.value);
  if (selected) {
    amountDollars.value = (selected.walletAmountMinor + selected.cardAmountMinor) / 100;
  }
};

const submit = async () => {
  if (!isValid.value || isSaving.value) return;
  isSaving.value = true;
  try {
    const response = await fetch(`/api/admin/wallets/${props.walletId}/adjustments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: kind.value,
        amountMinor: Math.round(amountDollars.value * 100),
        reason: reason.value.trim(),
        notes: notes.value.trim() || null,
        sourceTopUpId: sourceTopUpId.value,
        sourceRideId: sourceRideId.value
      })
    });
    if (!response.ok) {
      const message = await response.text();
      showToast({ title: 'Error', body: message || 'Unable to submit the request.', type: notificationType.ERROR, delay: 5000 });
      return;
    }
    const created = await response.json();
    const modalEl = document.getElementById(modalId.value);
    const instance = Modal.getInstance(modalEl) || new Modal(modalEl);
    instance.hide();
    reset();
    showToast({ title: 'Success', body: 'Request submitted for approval.', type: notificationType.SUCCESS });
    emit('created', created);
  } catch (error) {
    console.error('Error submitting wallet adjustment request:', error);
    showToast({ title: 'Oops..', body: 'Encountered an internal error. Please contact support.', type: notificationType.ERROR, delay: 7000 });
  } finally {
    isSaving.value = false;
  }
};
</script>
